import { VerificationSelectWrapper, VerificationRightWrapper, VerificationLeftWrapper } from '../PageHeader.styles';
import { useAuthStore } from '../../../context/AuthContext';
import StatsPanel from '../../verification/StatsPanel';
import { DropdownWrapper } from '../../../styles/panel.style';
import { MultiSelect } from 'react-multi-select-component';
import { useVerificationStore } from '../../../context/VerificationContext';
import { SelectOption } from '../../../types/ViewModel/SelectOption';
import { RoleTypeEnum } from '../../../enums/roleTypeEnum';
import { stringifyOptionsArray } from '../../../utils/verification/verificationUtils';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';
import { Separator } from 'src/App.styles';
import { DefaultFlexStrechedWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';

const PageHeaderVerificationComponent = () => {
    const { role } = useAuthStore();
    const {
        verificationFilter,
        initialSelectedVessels,
        initialSelectedUsers,
        selectedUsers,
        initialSelectedManagers,
        selectedManagers,
        filteredVerificationVesselList,
        selectedVessels,
    } = useVerificationStore();
    const { triggerQueryParamsUpdate } = useQueryParamsHandler();

    const showQcVerificationHeader = () =>
        location.pathname === '/verification' && (role.roleType === RoleTypeEnum.FleetPerformanceAnalyst || role.roleType === RoleTypeEnum.Admin);
    const showShipUserVerificationHeader = () => location.pathname === '/verification' && role.roleType === RoleTypeEnum.ShipUser;
    const showVerificationHeader = showQcVerificationHeader() || showShipUserVerificationHeader();

    const buildUsersParam = (users: SelectOption[]): QueryParamValueProps | null => {
        if (!users || users.length === 0) return { name: 'users', newValue: 'none' };
        if (initialSelectedUsers && users.length === initialSelectedUsers.length) return null;
        return { name: 'users', newValue: stringifyOptionsArray(users) };
    };
    const buildManagersParam = (managers: SelectOption[]): QueryParamValueProps | null => {
        if (!managers || managers.length === 0) return { name: 'managers', newValue: 'none' };
        if (initialSelectedManagers && managers.length === initialSelectedManagers.length) return null;
        return { name: 'managers', newValue: stringifyOptionsArray(managers) };
    };

    const onUserChange = (items: SelectOption[]) => {
        // Derive vessel subset purely for URL param; store will rebuild from URL effect
        const derived: SelectOption[] = [];
        filteredVerificationVesselList.forEach((r) => {
            if (items.find((u) => u.label === r.fleetPerformanceAlertResponsible)) {
                derived.push(new SelectOption(r.imoNumber.toString(), r.vesselName));
            }
        });
        const updates: QueryParamValueProps[] = [];
        if (items && initialSelectedUsers && items.length === initialSelectedUsers.length) updates.push({ name: 'users', clearParam: true });
        else {
            const uP = buildUsersParam(items);
            if (uP) updates.push(uP);
        }
        if (derived.length === 0) updates.push({ name: 'vessels', newValue: 'none' });
        else if (initialSelectedVessels && derived.length === initialSelectedVessels.length) updates.push({ name: 'vessels', clearParam: true });
        else updates.push({ name: 'vessels', newValue: stringifyOptionsArray(derived) });
        triggerQueryParamsUpdate(updates);
    };

    const onManagerChange = (items: SelectOption[]) => {
        const derived: SelectOption[] = [];
        filteredVerificationVesselList.forEach((r) => {
            if (items.find((u) => u.label === r.technicalOperator)) {
                derived.push(new SelectOption(r.imoNumber.toString(), r.vesselName));
            }
        });
        const updates: QueryParamValueProps[] = [];
        if (items && initialSelectedManagers && items.length === initialSelectedManagers.length) updates.push({ name: 'managers', clearParam: true });
        else {
            const mP = buildManagersParam(items);
            if (mP) updates.push(mP);
        }
        if (derived.length === 0) updates.push({ name: 'vessels', newValue: 'none' });
        else if (initialSelectedVessels && derived.length === initialSelectedVessels.length) updates.push({ name: 'vessels', clearParam: true });
        else updates.push({ name: 'vessels', newValue: stringifyOptionsArray(derived) });
        triggerQueryParamsUpdate(updates);
    };

    const onVesselChange = (items: SelectOption[]) => {
        const total = initialSelectedVessels ? initialSelectedVessels.length : 0;
        const isAll = total > 0 && items.length === total;
        const updates: QueryParamValueProps[] = [];
        if (isAll) updates.push({ name: 'vessels', clearParam: true });
        else if (items.length === 0) updates.push({ name: 'vessels', newValue: 'none' });
        else updates.push({ name: 'vessels', newValue: stringifyOptionsArray(items) });
        triggerQueryParamsUpdate(updates);
    };

    const filterOptions = (options: SelectOption[], filter: string) => {
        if (!filter) return options;
        const re = new RegExp(filter, 'i');
        return options.filter(({ label }) => label && label.match(re));
    };

    return (
        <>
            {showVerificationHeader && (
                <VerificationSelectWrapper>
                    <DefaultFlexStrechedWrapper>
                        <VerificationLeftWrapper>
                            <DropdownWrapper className="verification-header-select">
                                <label>Assignee</label>
                                <MultiSelect
                                    options={initialSelectedUsers}
                                    filterOptions={filterOptions}
                                    value={selectedUsers}
                                    onChange={onUserChange}
                                    labelledBy={'Select'}
                                    overrideStrings={{
                                        selectSomeItems: 'Select assignees',
                                        allItemsAreSelected: 'All assignees',
                                        selectAll: 'Select All',
                                        search: 'Search',
                                    }}
                                />
                            </DropdownWrapper>
                            <DropdownWrapper className="verification-header-select">
                                <label>Manager</label>
                                <MultiSelect
                                    options={initialSelectedManagers}
                                    filterOptions={filterOptions}
                                    value={selectedManagers}
                                    onChange={onManagerChange}
                                    labelledBy={'Select'}
                                    overrideStrings={{
                                        selectSomeItems: 'Select managers',
                                        allItemsAreSelected: 'All managers',
                                        selectAll: 'Select All',
                                        search: 'Search',
                                    }}
                                />
                            </DropdownWrapper>
                            <Separator />
                            <DropdownWrapper className="verification-header-select">
                                <label>Vessel</label>
                                <MultiSelect
                                    options={initialSelectedVessels}
                                    filterOptions={filterOptions}
                                    value={
                                        verificationFilter?.selectedVessels === null
                                            ? initialSelectedVessels
                                            : verificationFilter?.selectedVessels || selectedVessels || []
                                    }
                                    onChange={onVesselChange}
                                    labelledBy={'Select'}
                                    hasSelectAll={true}
                                    overrideStrings={{
                                        selectSomeItems: 'Select vessels',
                                        allItemsAreSelected: 'All vessels',
                                        selectAll: 'Select All',
                                        search: 'Search',
                                    }}
                                />
                            </DropdownWrapper>
                        </VerificationLeftWrapper>
                        <VerificationRightWrapper>
                            <StatsPanel />
                        </VerificationRightWrapper>
                    </DefaultFlexStrechedWrapper>
                </VerificationSelectWrapper>
            )}
        </>
    );
};

export default PageHeaderVerificationComponent;
